import React, { createContext, useCallback, useState } from 'react';

import Loading from '@justgiving/loading';

import Portal from '../components/Portal/Portal';
import { Toast, ToastProps } from '../components/Toast/Toast';

import styles from '../components/Loading/Loading.scss';
import Modal from '../components/Modal/Modal';

export type AppContext = {
  showToast(toast: ToastProps): void;
  hideToast(): void;
  startLoading(): void;
  stopLoading(): void;
  isSSR: boolean;
};

export const AppContext = createContext<AppContext | undefined>(undefined);
AppContext.displayName = 'AppContext';

export const AppContextProvider = AppContext.Provider;

export const AppProvider: React.FunctionComponent = ({ children }) => {
  const [toast, setToast] = useState<ToastProps | null>();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const hideToast = useCallback(() => {
    setToast(null);
  }, [setToast]);

  const showToast = useCallback(
    (toast: ToastProps) => {
      setToast(toast);
    },
    [setToast],
  );

  const startLoading = useCallback(() => {
    setIsLoading(true);
  }, [setIsLoading]);

  const stopLoading = useCallback(() => {
    setIsLoading(false);
  }, [setIsLoading]);

  const isSSR = typeof window === 'undefined';

  // TODO: Discuss whether this is the right location to add the toast notification
  return (
    <AppContextProvider
      value={{ showToast, hideToast, startLoading, stopLoading, isSSR }}
    >
      {children}
      {isLoading ? (
        <Modal className={styles.spinner}>
          <Loading />
        </Modal>
      ) : null}
      {toast ? (
        <Portal>
          <Toast onDismiss={hideToast} {...toast} />
        </Portal>
      ) : null}
    </AppContextProvider>
  );
};

AppProvider.displayName = 'AppProvider';
